import { CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/feedback";
import { ErrorState, LoadingState, NotAvailableState } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import type { OversightTone } from "./oversight-ui";

/**
 * The alerts register's state surface (MON-02, /monitoring/alerts).
 *
 * Display only. This component owns no fetching: the page/caller passes the
 * data or the failure, so every branch is exercised by a deterministic test and
 * no branch can quietly invent a value. It is deliberately separate from the
 * Overview/Provider panels so the alerts surface has exactly one place where
 * "empty" (a real API returned nothing), "loading", "error" (a real call
 * failed) and "not available" (no alert domain exists in this environment) are
 * distinguishable — see page-delivery-pipeline.md G2 `defer` and the
 * `NotAvailableState` doc comment in components/ui/system-state.tsx.
 */

export type AlertSeverity = "critical" | "warning" | "info";

export type MonitoringAlert = {
  id: string;
  title: string;
  severity: AlertSeverity;
  ownerService: string;
  /** ISO-8601 or already-formatted. Absent renders an honest "not reported". */
  raisedAt?: string;
  /** Runbook an operator needs before acting. Absent renders "no runbook linked". */
  runbookRef?: string;
};

export type AlertsError = {
  message: string;
  /** Only when the gateway actually returned one — never synthesised. */
  supportReference?: string;
};

/**
 * Severity is a text label first and a colour second. `severityLabel` is what
 * the badge renders and what a screen reader (and any colour-blind operator)
 * reads; the tone only reinforces it. Every severity value here has a label —
 * adding a severity without one is a type error, not a silent blank badge.
 */
export const SEVERITY_LABEL: Record<AlertSeverity, string> = {
  critical: "Critical",
  warning: "Warning",
  info: "Info",
};

const SEVERITY_TONE: Record<AlertSeverity, OversightTone> = {
  critical: "danger",
  warning: "warning",
  info: "info",
};

const NO_RUNBOOK = "No runbook linked";
const NO_RAISED_AT = "Not reported";

/**
 * A value that is present but blank is the same problem as an absent one: the
 * cell renders empty and the operator cannot tell "there is none" from "we did
 * not say". `??` treats `""` (and `"   "`) as a value, which is how an API
 * mapper that normalises a missing field to an empty string gets past it, so
 * both fields below go through this instead.
 */
function reportedValue(value: string | undefined, fallback: string): string {
  return value && value.trim() ? value : fallback;
}

export function AlertsStateView({
  alerts,
  loading,
  error,
  unavailableReason,
}: {
  /** `null` while unavailable/loading; `[]` means a real call returned no alerts. */
  alerts: MonitoringAlert[] | null;
  loading: boolean;
  error: AlertsError | null;
  /** Set when no alert domain exists in this environment (G2 defer). */
  unavailableReason?: string;
}) {
  if (unavailableReason) {
    return (
      <NotAvailableState
        title="Alerting is not available yet"
        description="There is no alert domain in this environment: no alert storage, no acknowledgement workflow, and no incident record."
        reason={unavailableReason}
      />
    );
  }

  if (loading) {
    return (
      <div>
        <p className="mb-3 text-sm text-muted">Loading alerts...</p>
        <LoadingState rows={3} />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        title="Alerts could not be loaded"
        description={error.message}
        // The reference is the one thing support can act on. When the gateway
        // did not return one we say so, rather than showing an empty label or
        // a made-up id.
        details={
          error.supportReference
            ? `Support reference: ${error.supportReference}`
            : "No support reference was returned for this failure."
        }
      />
    );
  }

  if (!alerts || alerts.length === 0) {
    return (
      <EmptyState
        tone="positive"
        icon={<CheckCircle2 aria-hidden className="size-5 text-success" />}
        title="No active alerts"
        description="Nothing is firing in this environment."
      />
    );
  }

  return (
    <TableWrapper>
      <Table className="dense">
        <thead>
          <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
            <th className="px-4 py-2.5">Severity</th>
            <th className="px-4 py-2.5">Alert</th>
            <th className="px-4 py-2.5">Owning service</th>
            <th className="px-4 py-2.5">Raised</th>
            <th className="px-4 py-2.5">Runbook</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((alert) => (
            <tr key={alert.id} className="border-b border-line last:border-b-0">
              <td className="px-4 py-2.5">
                <Badge variant={SEVERITY_TONE[alert.severity]} data-severity={alert.severity}>
                  {SEVERITY_LABEL[alert.severity]}
                </Badge>
              </td>
              <td className="px-4 py-2.5 text-[13px] font-semibold text-ink">{alert.title}</td>
              <td className="px-4 py-2.5 font-mono text-[13px] text-text">{alert.ownerService}</td>
              <td className="px-4 py-2.5 text-[13px] text-muted">
                {reportedValue(alert.raisedAt, NO_RAISED_AT)}
              </td>
              <td className="px-4 py-2.5 font-mono text-[13px] text-muted">
                {reportedValue(alert.runbookRef, NO_RUNBOOK)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
}
